import {
  finalizarFalaProgressiva as finalizarFalaProgressivaMotor,
  iniciarFalaProgressiva as iniciarFalaProgressivaMotor,
  motorDeVoz,
} from '@modules/aawz-intelligence-v2/utils/motor-de-voz'
// ⚠️ De `fala-da-ia.js` e não do motor: ele reexporta a fila de fala INTEIRA
// menos este símbolo (conferido no `export {}` de `motor-de-voz.js`), e
// importá-lo de lá daria `undefined` em runtime, sem erro de build.
import {
  esquecerDestinoDeAudio,
  interromperFalaDaIa,
  registrarDestinoDeAudio,
} from '@modules/aawz-intelligence-v2/utils/fala-da-ia'
import { apiAvatarAoVivo } from '@modules/aawz-intelligence-v2/api/apiAvatarAoVivo'
import { criarSessaoAvatarAoVivo } from '@modules/aawz-intelligence-v2/utils/avatar-ao-vivo/sessao-avatar-ao-vivo'
import { pcmComoFonteDeAudio } from '@modules/aawz-intelligence-v2/utils/avatar-ao-vivo/pcm-para-wav'

/**
 * ★ O INTERRUPTOR DA VOZ DO PRIMEIRO CONTATO. Uma linha, e só ela.
 *
 * Owner, 18/09/2026, olhando a tela: *"o wayne está com voz do gpt live;
 * vamos retirar isso, deixar só o texto por enquanto"*.
 *
 * Desligado, o primeiro contato é a MENSAGEM ESCRITA: o roteiro inteiro na
 * tela, "Continuar" no fim, e nada é falado. Duas coisas deixam de acontecer,
 * e as duas de propósito:
 *
 *  1. a boas-vindas não pede a síntese (`:sem-audio`) — é o caminho
 *     `CANAL_TEXTO`, que já existia para reabertura e para falha do áudio;
 *  2. a sessão do avatar não abre. **Sem áudio não há lábios a sincronizar**:
 *     o LiveAvatar LITE anima a boca a partir do PCM que mandamos, então o
 *     Wayne ficaria parado — e parado consumindo o teto de 60 s da sessão.
 *
 * Ligar de volta é trocar `false` por `true`. Nada mais foi removido: o
 * provedor, o protocolo, o som local e os testes continuam inteiros, porque o
 * que está em julgamento é a VOZ, não a máquina que a entrega.
 *
 * ⚠️ O modo de voz de sempre (GPT Live) não passa por aqui e não muda —
 * *"NÃO ALTERE ESSE CAMINHO"*.
 */
export const FALA_DO_PRIMEIRO_CONTATO = false

// ★ A CONVERSA COM O AVATAR — a porta falada do PRIMEIRO CONTATO (spec-009).
//
// Direção do owner, 18/09/2026: *"nesse primeiro chat, a conversa por voz seja
// com o avatar, nao com o modo voz (mas só entrada ser diferente, o fluxo e
// racional permanece); e faça o modo voz não atrapalhar e mexer o minimo
// possível no modo chat (ser só uma porta diferente)"*.
//
// ## Por que o avatar e não o modo de voz — o fio mediu
//
// No transporte em TEMPO REAL, o provedor **descarta o que mandamos enquanto a
// delegação está aberta**: 26 `session.commentary.append` enviados, 0 ecoados
// ([T071]); e o desfecho da consulta, que volta pelo `function_call_output`,
// também não é verbalizado — medido no fio em 18/09 (`promessa-exec75`:
// `response.item.create` 1, `session.delegation.created` 2, **0 deltas de fala
// depois**, `erros_do_provedor: []`). Ou seja: **naquele caminho a voz não tem
// como falar a resposta** sem o servidor fatiar o turno.
//
// Aqui nada disso existe, e o motivo é estrutural: a fala do avatar sai do TTS
// de produção (`apiVoz.sintetizar`) para um `<audio>` NOSSO — **fora do data
// channel**. Não há delegação, não há descarte, e não há a segunda voz que o
// modo de voz traria junto.
//
// ## "Só a entrada é diferente": este mixin NÃO reimplementa envio nenhum
//
// O ciclo é o mesmo par que `onEnviarVoz` e `responderPerguntas` já usam —
// `iniciarFalaProgressivaMotor` antes, `modoVoz: true` no corpo, e
// `finalizarFalaProgressivaMotor` no `finally`:
//
// - `modo_voz: true` é o que faz o backend emitir a versão FALADA do turno (o
//   evento SSE `fala`, voz v3), que é a **consolidação** da resposta — curta,
//   feita com o contexto todo, e diferente do texto escrito;
// - a fila de `fala-da-ia.js` sintetiza e toca na ordem, com o fallback da 1ª
//   frase do texto escrito (sem markdown) quando o evento não vier;
// - o texto continua aparecendo na tela pelo caminho de sempre, e é ele a
//   **fonte auditável** — a fala é resumo (G-14: o áudio diverge do próprio
//   transcript em 2 de 31 sessões).
//
// ⚠️ **`finally`, e não depois do `await`**: `enviarMensagem` PROPAGA exceção
// (`despacharMensagem` não tem try/catch), e um turno que falha sem
// `finalizarFalaProgressivaMotor` trava `_aceitandoDeltas` em `true` PARA
// SEMPRE — foi o "o modo voz ainda está parando no meio do processo" do owner
// (Hotfix P1, 09/09). Mesmo padrão dos dois irmãos.
//
// ⚠️ **Nenhuma sessão de voz é aberta, e `motorDeVoz.sessaoDeVozAtiva` fica
// FALSA.** É isso que mantém o modo de voz sendo "só uma porta diferente": o
// composer não troca de aparência, o barge-in local não arma (guarda em
// `transporte-por-turnos.js`) e o observador de silêncio não roda.
//
// ## O ROSTO AO VIVO (LiveAvatar LITE) — a camada opcional de cima
//
// Direção do owner, 18/09/2026: *"quero o liveavatar lite só para as boas
// vindas e para ter a conversa inicial... todo o resto e falas no modo voz é
// via gpt live (NÃO ALTERE ESSE CAMINHO!)"*.
//
// O que muda quando ele liga: **o destino do áudio**, e só. A fala continua
// saindo do nosso TTS, frase a frase, na mesma fila — ela é que passa a ir ao
// provedor (`agent.speak`), que devolve o rosto falando aquele áudio, com os
// lábios sincronizados. Segmentação, ordem, geração, interrupção e re-arme
// ficam onde estavam.
//
// ★ **A degradação é o contrato, e é o que torna esta branch descartável.**
// Se a sessão não abrir — sem chave no servidor, provedor fora, crédito no
// fim, WebSocket bloqueado, SDK de vídeo recusado —, NADA é registrado e a
// conversa segue exatamente como antes: `<audio>` local com MP3, retrato ou
// selo no lugar do rosto. Nenhum caminho aqui pode derrubar o turno.
//
// ⚠️ **O Modo de Voz não é tocado por nada disto.** Ele nem passa por esta
// fila: lá quem fala é o provedor do GPT-Live, pelo data channel. Apagar este
// arquivo inteiro devolve o comportamento anterior, byte a byte.
export const viewChatConversaComAvatarMixin = {
  data() {
    return {
      // A conversa falada com o avatar está em curso? Ligada ao fim da
      // boas-vindas do primeiro contato; desligada quando o consultor sai da
      // conversa ou troca de tela. Escopo de TELA, como `boasVindasEncerrada`.
      conversaComAvatarAtiva: false,
      // As faixas que voltam da sala do provedor. `null` é o estado normal:
      // significa "sem rosto ao vivo", e a tela cai no clipe/retrato/selo.
      faixaDeVideoDoAvatar: null,
      faixaDeAudioDoAvatar: null,
      // A sessão está de pé no provedor? Só para a tela poder dizer, no
      // rótulo, que o rosto é ao vivo — nunca para decidir fluxo.
      avatarAoVivoLigado: false,
    }
  },
  computed: {
    /**
     * ★ QUEM FALA A APRESENTAÇÃO — o contrato que `boas-vindas-falada.vue`
     * consome (spec-009).
     *
     * Sem ele, o rosto ao vivo só entrava DEPOIS da boas-vindas, e no instante
     * em que o owner olhava a tela mostrava o clipe de fundo. O pedido dele
     * nomeia as duas coisas: *"só para as boas vindas E para ter a conversa
     * inicial"*.
     *
     * É uma referência ESTÁVEL (`this._falanteDoAvatar`), não um objeto novo a
     * cada render: o player guarda o que recebe, e uma prop que troca de
     * identidade a cada tick faria o watcher dele remontar a fala.
     */
    /**
     * ★ O CLIPE DE FUNDO, e por que esta branch NÃO o pede (18/09/2026).
     *
     * O clipe do Viggle é gerado por prompt, e o prompt termina em
     * **`no people`** (`core/clipe_viggle.py`) — ele nunca foi um rosto, são
     * partículas de luz navy/gold. O owner pediu *"um vídeo meu"*; o que a
     * tela mostrava era isso. Quem mediu foi o teammate `avatar-ajustes-do-
     * owner`, lendo o backend, depois de consertar a fiação que o front nunca
     * chamava.
     *
     * Aqui o rosto é AO VIVO e fala a apresentação. Então o clipe:
     *
     * 1. não tem onde entrar — `temAoVivo` vence `temClipe` nos dois
     *    componentes, e sem sessão o honesto é o retrato/selo, não um fundo
     *    abstrato que ninguém pediu;
     * 2. **custa dinheiro**: a rota dispara geração PAGA a cada primeiro
     *    contato, por profissional (US$ 0,01/s). Pedi-lo aqui seria pagar
     *    por um vídeo que a tela nunca mostra.
     *
     * Devolver `null` é o que impede a chamada: `resolverClipe()` sai na
     * primeira linha sem provedor, e o `GET` — que é o gatilho da geração —
     * nunca sai. **Reverter é trocar este `null` por `this.provedorDoClipe`**;
     * o provedor segue montado no mixin irmão, intocado.
     */
    provedorDoClipeDaApresentacao() {
      return null
    },
    /**
     * A tela ainda quer o rosto? Vale nos DOIS momentos em que ele aparece:
     * durante a apresentação e na conversa que vem depois.
     */
    avatarAindaTemTela() {
      return Boolean(this.mostrarBoasVindas || this.conversaComAvatarAtiva)
    },
    falanteDoAvatar() {
      if (!this._falanteDoAvatar) {
        this._falanteDoAvatar = {
          prontoEm: (ms) => this.avatarProntoEm(ms),
          falar: (pcm) => this.falarComAvatar(pcm),
          interromper: () => this.interromperAvatar(),
        }
      }
      return this._falanteDoAvatar
    },
    /**
     * O primeiro contato fala? Lido pela tela para decidir `:sem-audio`.
     *
     * Um computed e não a constante direta no template: o `view-chat.vue` não
     * importa este módulo, e um dia isto pode virar uma decisão do servidor
     * (funcionalidade do escritório) sem que a tela mude uma linha.
     */
    falaDoPrimeiroContatoLigada() {
      return FALA_DO_PRIMEIRO_CONTATO
    },
    /**
     * O avatar está falando AGORA.
     *
     * Lido do estado compartilhado que a própria fila de fala mantém
     * (`fala-da-ia.js` põe `'ia'` ao tocar e limpa no fim) — não há flag nova,
     * e por isso ele acompanha TODOS os caminhos de parada (fim natural,
     * interrupção, falha do áudio) sem ninguém precisar avisar.
     */
    avatarFalando() {
      // `motorDeVoz` é um `new Vue()` usado como store — ler o campo aqui é
      // reativo, e por isso o computed acompanha a fala sem watcher nenhum.
      return this.conversaComAvatarAtiva && motorDeVoz.estadoDaFala === 'ia'
    },
  },
  watch: {
    /**
     * ★ A sessão nasce quando a APRESENTAÇÃO APARECE, não quando ela acaba.
     *
     * Era no fim, e por isso o rosto ao vivo só entrava depois — no instante
     * em que o owner olhava, a tela mostrava o clipe de fundo. Abrir aqui dá
     * ao provedor os segundos de que ele precisa (1,0-2,6 s medidos) enquanto
     * o roteiro já está sendo lido na tela, e é o que permite ao avatar FALAR
     * a apresentação.
     *
     * ⚠️ `mostrarBoasVindas` vem do mixin irmão (`view-chat-boas-vindas`) —
     * os dois se fundem no mesmo componente. Sem a boas-vindas na tela, nada
     * é aberto: a sessão é paga por minuto e não se abre "por via das
     * dúvidas".
     */
    mostrarBoasVindas(mostra) {
      if (mostra) this.abrirAvatarAoVivo()
    },
  },
  beforeDestroy() {
    // Lido por `abrirAvatarAoVivo` depois do `await`: a tela pode morrer
    // enquanto a sessão abre, e aí não há a quem mostrar o rosto.
    this._destruido = true
    // A fala é um recurso do browser: sair da tela com o áudio tocando
    // deixaria o avatar falando sobre uma conversa que não está mais aberta.
    if (this.conversaComAvatarAtiva) this.encerrarConversaComAvatar()
    // Sessão aberta sem a conversa ativa (abertura em voo) também precisa
    // morrer — `derrubarAvatarAoVivo` é idempotente.
    this.derrubarAvatarAoVivo()
  },
  methods: {
    /**
     * Liga a conversa falada. Chamado quando a boas-vindas termina — é o
     * instante em que a casa acabou de falar e a vez passa a ser da pessoa.
     *
     * O rosto ao vivo é pedido SEM ESPERA: a conversa já está ligada quando
     * esta linha retorna, e o avatar entra na tela quando (e se) a sessão
     * abrir. Esperar por ele atrasaria a única coisa que importa aqui.
     */
    iniciarConversaComAvatar() {
      this.conversaComAvatarAtiva = true
      this.abrirAvatarAoVivo()
    },

    /**
     * Abre a sessão do provedor e passa a mandar a fala para lá.
     *
     * NUNCA lança e nunca rejeita: todo desfecho ruim termina no caminho de
     * hoje (`<audio>` local), e o consultor não vê erro nenhum — ele não
     * pediu um avatar, pediu uma conversa.
     */
    async pedirSessaoDoAvatar() {
      try {
        const r = await apiAvatarAoVivo.abrir()
        if (!r || r.status !== 200 || !r.value) return null
        // Saiu da tela enquanto a sessão abria: encerra o que acabou de
        // nascer em vez de deixar uma sessão paga viva sem ninguém olhando.
        //
        // ⚠️ **O predicado é "a tela ainda quer o rosto", NÃO
        // `conversaComAvatarAtiva`.** Essa flag só liga no FIM da boas-vindas,
        // e desde que a sessão passou a nascer no INÍCIO dela a guarda
        // encerrava tudo no mesmo instante: o servidor via o POST, o
        // navegador nunca abria o WebSocket, e nada acusava — a tela
        // simplesmente ficava sem rosto. Medido pela sonda de fluxo em
        // 18/09/2026 (4/13 elos, `enviados: {}` e `recebidos: {}`).
        if (!this.avatarAindaTemTela || this._destruido) {
          apiAvatarAoVivo.encerrar({ idSessao: r.value.id_sessao })
          return null
        }
        return r.value
      } catch (e) {
        return null
      }
    },

    abrirAvatarAoVivo() {
      // Voz desligada: nada a sincronizar, e sessão é paga por minuto.
      // Lê o COMPUTED, não a constante: é o que permite aos testes exercitar a
      // máquina inteira enquanto o interruptor está desligado — e é o ponto
      // em que uma decisão do servidor entraria, se um dia entrar.
      if (!this.falaDoPrimeiroContatoLigada) return Promise.resolve()
      if (this._aberturaDoAvatar) return this._aberturaDoAvatar
      // A promessa é GUARDADA: `avatarProntoEm` espera por ela, e uma segunda
      // chamada (o watcher e o fim da boas-vindas) reusa a mesma abertura em
      // vez de criar uma sessão paga a mais.
      this._aberturaDoAvatar = this._abrirSessaoDoAvatar()
      return this._aberturaDoAvatar
    },

    async _abrirSessaoDoAvatar() {
      if (this._sessaoDoAvatar) return
      const credenciais = await this.pedirSessaoDoAvatar()
      if (!credenciais) {
        // ⚠️ Sem isto, a promessa da tentativa que FALHOU fica em cache e
        // `abrirAvatarAoVivo` a devolve para sempre: o fim da boas-vindas
        // pediria de novo e receberia o fracasso de antes, sem nenhuma
        // chamada nova sair.
        this._aberturaDoAvatar = null
        return
      }

      this._idSessaoDoAvatar = credenciais.id_sessao
      const sessao = criarSessaoAvatarAoVivo()
      this._sessaoDoAvatar = sessao

      const abriu = await sessao.abrir(credenciais, {
        aoVideo: (faixa) => {
          this.faixaDeVideoDoAvatar = faixa
        },
        aoAudio: (faixa) => {
          this.faixaDeAudioDoAvatar = faixa
        },
        aoQueda: () => this.derrubarAvatarAoVivo(),
      })
      if (!abriu) {
        this.derrubarAvatarAoVivo()
        return
      }

      // ★ A COSTURA. A partir daqui a fila de fala manda o PCM para o
      // provedor em vez de tocar o `<audio>`. É a única linha que muda o
      // comportamento da fala — e desfazê-la devolve tudo ao que era.
      this._destinoDeAudio = registrarDestinoDeAudio({
        pedePcm: true,
        falar: (pcmBase64) => sessao.falar(pcmBase64),
        interromper: () => sessao.interromper(),
      })
      this.avatarAoVivoLigado = true
    },

    /**
     * A sessão está de pé dentro de `ms`?
     *
     * Quem chama é a boas-vindas, para decidir se pede PCM ou MP3. O texto
     * dela já está na tela, então o que este prazo atrasa é a VOZ, nunca a
     * mensagem — e estourado, a apresentação sai como sempre saiu.
     */
    avatarProntoEm(ms) {
      if (this.avatarAoVivoLigado) return Promise.resolve(true)
      if (!this._aberturaDoAvatar) return Promise.resolve(false)
      // `Promise.race` com um prazo: a abertura segue em voo, e se ela chegar
      // depois o rosto entra para a CONVERSA — só não fala a apresentação.
      return Promise.race([
        this._aberturaDoAvatar.then(() => this.avatarAoVivoLigado),
        new Promise((resolve) => setTimeout(() => resolve(false), ms)),
      ])
    },
    /**
     * Manda o PCM ao provedor e resolve quando ele TERMINA de falar.
     *
     * ★ **O SOM sai daqui, não da sala.** Medido em 18/09/2026 com o SDK
     * instrumentado: o LiveAvatar LITE publica **só vídeo** —
     * `video:live:enabled=true` chega, `trackSubscribed` de áudio nunca. Faz
     * sentido no modo LITE: quem produz o áudio somos nós, e ele só renderiza
     * os lábios a partir dele. O owner viu em 30 segundos: *"video está sem
     * som"*, com o rosto renderizando e a legenda andando.
     *
     * ⚠️ E o som começa no `agent.speak_started`, não no envio: é quando a
     * boca de fato se mexe. Tocar no envio deixaria a fala à frente dos
     * lábios pelo tempo de codificação e rede do vídeo.
     */
    falarComAvatar(pcmBase64) {
      if (!this._sessaoDoAvatar) return Promise.resolve()
      return this._sessaoDoAvatar
        .falar(pcmBase64, { aoComecar: () => this.tocarSomDoAvatar(pcmBase64) })
        .then(() => this.calarSomDoAvatar())
    },

    /**
     * Toca o MESMO PCM que foi ao provedor, embrulhado em WAV.
     *
     * Elemento de instância, fora do `data()`: é recurso do browser, não
     * estado de tela — deixá-lo reativo faria o Vue observar um objeto do
     * browser inteiro a cada quadro (mesma razão do `_audio` do player).
     */
    tocarSomDoAvatar(pcmBase64) {
      const fonte = pcmComoFonteDeAudio(pcmBase64)
      if (!fonte) return
      this.calarSomDoAvatar()
      try {
        const som = new Audio(fonte)
        this._somDoAvatar = som
        // Autoplay recusado não derruba nada: o rosto continua e a legenda
        // também. O som é o que se perde, e é um estado previsto.
        const promessa = som.play()
        if (promessa && typeof promessa.catch === 'function') {
          promessa.catch(() => {})
        }
      } catch (e) {
        // Ambiente sem `Audio` (SSR, teste): segue sem som.
      }
    },

    /** Cala o som local. Idempotente. */
    calarSomDoAvatar() {
      if (!this._somDoAvatar) return
      const som = this._somDoAvatar
      this._somDoAvatar = null
      try {
        som.pause()
      } catch (e) {
        // Elemento já morto: o desfecho é o mesmo.
      }
    },

    interromperAvatar() {
      // ⚠️ Os DOIS lados: o provedor tem áudio no buffer dele, e nós temos um
      // `<audio>` tocando. Calar só um deixaria metade da fala no ar.
      this.calarSomDoAvatar()
      if (this._sessaoDoAvatar) this._sessaoDoAvatar.interromper()
    },

    /**
     * Desliga o rosto ao vivo e devolve a fala ao `<audio>` local.
     *
     * Chamado por queda do canal, por falha de abertura e pelo encerramento
     * normal. Idempotente de propósito: os três podem acontecer juntos.
     */
    derrubarAvatarAoVivo() {
      if (this._destinoDeAudio) {
        // Guarda de IDENTIDADE (mesma dos provedores do `apiChat`): só
        // esquece se o destino registrado ainda for o NOSSO.
        esquecerDestinoDeAudio(this._destinoDeAudio)
        this._destinoDeAudio = null
      }
      if (this._sessaoDoAvatar) {
        this._sessaoDoAvatar.fechar()
        this._sessaoDoAvatar = null
      }
      if (this._idSessaoDoAvatar) {
        // Fire-and-forget: quem fechou a tela já saiu, e a sessão do provedor
        // expira sozinha se esta chamada falhar.
        apiAvatarAoVivo.encerrar({ idSessao: this._idSessaoDoAvatar })
        this._idSessaoDoAvatar = null
      }
      this.calarSomDoAvatar()
      this.faixaDeVideoDoAvatar = null
      this.faixaDeAudioDoAvatar = null
      this.avatarAoVivoLigado = false
      // Sem isto, `abrirAvatarAoVivo` devolveria para sempre a promessa da
      // sessão que acabou de morrer, e nenhuma outra abriria.
      this._aberturaDoAvatar = null
    },
    /**
     * Desliga, e cala o que estiver tocando. Idempotente: sair da tela e o
     * consultor encerrar são o mesmo desfecho.
     */
    encerrarConversaComAvatar() {
      this.conversaComAvatarAtiva = false
      // A ORDEM importa: interromper ANTES de derrubar faz o barge-in chegar
      // ao provedor pelo destino ainda registrado. Invertida, o avatar
      // seguiria falando o que já estava no buffer dele.
      interromperFalaDaIa()
      this.derrubarAvatarAoVivo()
    },
    /**
     * ★ O turno FALADO do avatar — a pessoa respondeu (digitando ou ditando),
     * e a resposta vai ser falada de volta.
     *
     * ⚠️ **Os anexos passam por aqui inteiros.** O turno é o MESMO da
     * digitação: trocar a porta de entrada não pode engolir o arquivo que ela
     * acabou de anexar — seria o envio sumindo sem aviso, que é exatamente o
     * defeito que a spec-008 mediu no guard mudo de `enviarMensagem`.
     *
     * @param {string} texto o que ela escreveu ou ditou.
     * @param {Array=} anexos a fila do composer, como a digitação a manda.
     * @param {Array=} anexosProntos idem — os que já subiram.
     * @returns {Promise<boolean>} o desfecho de `enviarMensagem` (ADITIVO
     *   desde a spec-008: `true` despachado, `false` recusado no guard).
     */
    async enviarComAvatar(texto, anexos, anexosProntos) {
      if (!texto) return false
      // A voz escolhida para ESTA conversa (spec-016 US-1) viaja ao motor, que
      // a repassa a cada `apiVoz.sintetizar`. Sem escolha, o motor não manda
      // `voice_id` e o backend usa a padrão.
      const geracao = iniciarFalaProgressivaMotor(this.vozSelecionadaAtual)
      try {
        return await this.enviarMensagem(texto, anexos, anexosProntos, { modoVoz: true })
      } finally {
        finalizarFalaProgressivaMotor(geracao)
      }
    },
  },
}
